import React, { useEffect, useMemo, useState } from 'react';
import { DEV_URL, rpost } from '../../../utils/requests';
import { useShowSnackbar } from '../../../hooks';
import { Card, Row, Col, Button } from 'react-bootstrap';

const SystemAdminSettings = () => {
    const showSnackbar = useShowSnackbar();
    const [loading, setLoading] = useState(false);
    const [addons, setAddons] = useState([]);
    const [clients, setClients] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [dirtyByClient, setDirtyByClient] = useState({}); // clientId -> Set(addOnId)

    const clientIdToSelected = useMemo(() => {
        const map = {};
        clients.forEach(c => { map[c.clientID || c.ClientID] = new Set(); });
        mappings.forEach(m => {
            const cid = m.clientID || m.ClientID; const aid = m.addOnID || m.AddOnID;
            if (!map[cid]) map[cid] = new Set();
            map[cid].add(aid);
        });
        return map;
    }, [clients, mappings]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            // fetch-only: empty body now supported by API
            let response = await rpost(`${DEV_URL}/api/client/addons-and-clients`, {});
            const data = response?.data;
            const payload = data?.data || data?.Data || data; // support both wrappers
            setAddons(payload.addons || payload.Addons || []);
            setClients(payload.clients || payload.Clients || []);
            setMappings(payload.mappings || payload.Mappings || []);
        } catch (e) {
            showSnackbar('Failed to load addons and clients', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const toggle = (clientId, addOnId) => {
        setDirtyByClient(prev => {
            const next = { ...prev };
            const key = clientId;
            const base = new Set(clientIdToSelected[key] || []);
            const current = new Set(next[key] ?? base);
            if (current.has(addOnId)) current.delete(addOnId); else current.add(addOnId);
            next[key] = current;
            return next;
        });
    };

    const saveAll = async () => {
        const dirtyClientIds = Object.keys(dirtyByClient);
        if (dirtyClientIds.length === 0) {
            showSnackbar('Nothing to save', 'info');
            return;
        }
        setLoading(true);
        try {
            // Save sequentially to keep API simple and show one final refresh
            for (const clientId of dirtyClientIds) {
                const current = dirtyByClient[clientId] ?? clientIdToSelected[clientId] ?? new Set();
                const addOnIds = Array.from(current);
                await rpost(`${DEV_URL}/api/client/addons-and-clients`, { clientId, addOnIds });
            }
            await fetchAll();
            setDirtyByClient({});
            showSnackbar('Saved changes', 'success');
        } catch (e) {
            const msg = e?.response?.data || 'Failed to save changes';
            showSnackbar(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Row>
            <Col md={12}>
                <h4 className="settings-page__section-title">Addons Access</h4>
                <Card>
                    {/* <Card.Header>
                        <Row className="align-items-center">
                            <Col className="text-end">
                                <Button variant="outline-secondary" size="sm" className="me-2" onClick={fetchAll} disabled={loading}>Refresh</Button>
                                <Button variant="primary" size="sm" onClick={saveAll} disabled={loading}>Save</Button>
                            </Col>
                        </Row>
                    </Card.Header> */}
                    <Card.Body>
                        {loading && <div className="mb-2" style={{ fontSize: 12 }}>Loading...</div>}
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered" style={{ fontSize: 12 }}>
                                <thead>
                                    <tr>
                                        <th style={{ whiteSpace: 'nowrap', padding: '6px 8px' }}>Client</th>
                                        {addons.map(a => (
                                            <th key={a.addOnID || a.AddOnID} style={{ whiteSpace: 'nowrap', padding: '6px 8px' }}>{a.name || a.Name}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients.map(c => {
                                        const cid = c.clientID || c.ClientID;
                                        return (
                                            <tr key={cid}>
                                                <td style={{ padding: '6px 8px' }}>{c.clientName || c.ClientName}</td>
                                                {addons.map(a => {
                                                    const aid = a.addOnID || a.AddOnID;
                                                    const baseChecked = clientIdToSelected[cid]?.has(aid);
                                                    const dirtySet = dirtyByClient[cid];
                                                    const isChecked = dirtySet ? dirtySet.has(aid) : baseChecked;
                                                    return (
                                                        <td key={aid} style={{ textAlign: 'center', padding: '6px 8px' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!isChecked}
                                                                onChange={() => toggle(cid, aid)}
                                                                style={{ width: 14, height: 14, cursor: 'pointer' }}
                                                            />
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card.Body>
                    <Card.Footer>
                        <Row className="align-items-center">
                            <Col className="text-end">
                                <Button variant="outline-secondary" size="sm" className="me-2" onClick={fetchAll} disabled={loading}>Refresh</Button>
                                <Button variant="primary" size="sm" onClick={saveAll} disabled={loading}>Save</Button>
                            </Col>
                        </Row>
                    </Card.Footer>
                </Card>
            </Col>
        </Row>
    );
};

export default SystemAdminSettings;


